"use client";

import { c as _c } from "react/compiler-runtime";
import { IconPlay } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconAlertWarningOutline18, IconArrowRotateAnticlockwiseOutline18, IconCheckOutline18, IconClockOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";

/**
 * Shared test panel for running action tests.
 * Used by both TestTab (workflow editor) and Integration detail page.
 * Contains: JSON input editor, connector selector, run test button, result display.
 */

import ConfirmDialog from "@/components/ConfirmDialog";
import { summarizeError, UnifiedErrorPanel } from "@/components/errors/UnifiedErrorPanel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useActionTest } from "@/hooks/useActionTest";
import { getSyntaxHighlightingStyle } from "@/lib/syntax-highlighting-style";
import { useColorMode } from "@chakra-ui/react";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import ConnectorSelector from "./ConnectorSelector";
import JsonEditorBase from "./form-fields/JsonEditorBase";

// --- Props ---
// Each consumer passes pre-processed data.
// TestTab: resolves variables from node.data.payload → defaultInputs
// Integration page: generates from payloadSchema → defaultInputs
interface ActionTestPanelProps {
  /** Action type identifier, e.g. "gmail-send-email" */
  actionType: string;
  /** Pre-processed inputs to populate the JSON editor */
  defaultInputs: Record<string, unknown>;
  /** Connector type for filtering, e.g. "pipedream:gmail" */
  connectorType?: string;
  /** Whether a connector must be selected to run test */
  connectorRequired?: boolean;
  /** Pre-selected connector ID (from workflow node config) */
  defaultConnectorId?: string | null;
  /** Whether this action supports testing (default: true) */
  isTestable?: boolean;
  /** Reason shown when testing is disabled */
  testDisabledReason?: string;
  /** Description text shown above the JSON editor */
  inputDescription?: string;
  /** Callback when user edits test inputs (used by TestTab to turn off variable toggle) */
  onInputsChange?: () => void;
  /** Ref that exposes a function to trigger the test programmatically */
  runTestRef?: React.MutableRefObject<(() => void) | null>;
}
function ResultJsonBlock(t0) {
  const $ = _c(6);
  const {
    data,
    syntaxStyle
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      margin: 0,
      padding: "0.75rem",
      fontSize: "0.75rem",
      lineHeight: "1.5",
      background: "transparent"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== data) {
    t2 = JSON.stringify(data, null, 2);
    $[1] = data;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== syntaxStyle || $[4] !== t2) {
    t3 = <div className="dark:bg-ds-neutral-800/50 max-h-64 overflow-y-auto bg-zinc-50"><SyntaxHighlighter language="json" style={syntaxStyle} customStyle={t1} wrapLines={true}>{t2}</SyntaxHighlighter></div>;
    $[3] = syntaxStyle;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export default function ActionTestPanel({
  actionType,
  defaultInputs,
  connectorType,
  connectorRequired = false,
  defaultConnectorId = null,
  isTestable = true,
  testDisabledReason,
  inputDescription = "Edit the JSON to set test values.",
  onInputsChange,
  runTestRef
}: ActionTestPanelProps) {
  // --- Theme for syntax highlighting in result display ---
  const {
    colorMode
  } = useColorMode();
  const isDark = colorMode === "dark";
  const syntaxStyle = getSyntaxHighlightingStyle(isDark);

  // --- Test API hook ---
  // Handles POST /v1/actions/{actionType}/test and tracks loading/result/error state
  const {
    testAction,
    isLoading,
    testResult,
    resetTest
  } = useActionTest();

  // --- Consecutive failure tracking ---
  const consecutiveFailuresRef = useRef(0);
  const [showFailureWarning, setShowFailureWarning] = useState(false);

  // Track consecutive failures from test results
  useEffect(() => {
    if (testResult.status === "error") {
      consecutiveFailuresRef.current += 1;
    } else if (testResult.status === "success") {
      consecutiveFailuresRef.current = 0;
    }
  }, [testResult.status]);

  // --- Test inputs state ---
  // JSON string for the editor. Initialized from defaultInputs prop.
  const [testInputsJson, setTestInputsJson] = useState<string>(() => JSON.stringify(defaultInputs, null, 2));

  // Ref to always hold the latest JSON string (avoids stale closure in handleRunTest)
  const testInputsJsonRef = useRef(testInputsJson);
  useEffect(() => {
    testInputsJsonRef.current = testInputsJson;
  }, [testInputsJson]);

  // Re-sync when parent provides new defaults
  // (e.g. TestTab changes variable resolution, or integration page changes)
  useEffect(() => {
    setTestInputsJson(JSON.stringify(defaultInputs, null, 2));
  }, [defaultInputs]);

  // Parse JSON string back to object for the API call. null = invalid JSON.
  const parsedTestInputs = useMemo(() => {
    try {
      return JSON.parse(testInputsJson) as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [testInputsJson]);

  // Notify parent when user edits JSON (e.g. TestTab disables "Use values from last run")
  const handleJsonChange = (value: string) => {
    setTestInputsJson(value);
    onInputsChange?.();
  };

  // Reset editor back to defaults
  const handleResetInputs = () => {
    setTestInputsJson(JSON.stringify(defaultInputs, null, 2));
  };

  // --- Connector state ---
  // Local selection — changing connector here does NOT affect the workflow config
  const [testConnectorId, setTestConnectorId] = useState<string | null>(defaultConnectorId);

  // Sync when parent's default changes (e.g. user picks different connector in Configure tab)
  useEffect(() => {
    setTestConnectorId(defaultConnectorId);
  }, [defaultConnectorId]);

  // --- Can we run the test? All conditions must pass. ---
  const canRunTest = useMemo(() => {
    if (!isTestable) return false;
    if (isLoading) return false;
    if (connectorRequired && !testConnectorId) return false;
    if (!parsedTestInputs) return false;
    return true;
  }, [isTestable, isLoading, connectorRequired, testConnectorId, parsedTestInputs]);

  // --- Execute test via API ---
  // Parse fresh from the ref to avoid stale closure when defaultInputs
  // are still propagating (e.g. on first click in create mode).
  const executeTest = useCallback(async () => {
    let inputs: Record<string, unknown>;
    try {
      inputs = JSON.parse(testInputsJsonRef.current);
    } catch {
      return;
    }
    try {
      await testAction({
        actionType,
        inputs,
        connector: testConnectorId ? {
          id: testConnectorId,
          type: connectorType || ""
        } : undefined
      });
    } catch {
      // Error already captured in testResult by the hook
    }
  }, [actionType, testConnectorId, connectorType, testAction]);

  // Expose executeTest via ref for external callers (e.g. "Run Test" button in error panel)
  useEffect(() => {
    if (runTestRef) {
      runTestRef.current = executeTest;
    }
    return () => {
      if (runTestRef) runTestRef.current = null;
    };
  }, [runTestRef, executeTest]);
  const handleRunTest = useCallback(() => {
    // Show warning after 3+ consecutive failures
    if (consecutiveFailuresRef.current >= 3) {
      setShowFailureWarning(true);
      return;
    }
    executeTest();
  }, [executeTest]);
  const handleRunTestAnyway = useCallback(() => {
    setShowFailureWarning(false);
    executeTest();
  }, [executeTest]);
  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  // --- Render ---
  return <div className="space-y-4">
      {/* JSON input editor with reset button */}
      <div className="relative">
        <JsonEditorBase label="Test Inputs" description={inputDescription} value={testInputsJson} onChange={handleJsonChange} placeholder="{}" minHeight="160px" showValidationInline={true} />
        <button onClick={handleResetInputs} className="absolute right-0 top-0 flex items-center gap-1 text-xs font-medium uppercase tracking-wider text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300" title="Reset to default values">
          <IconArrowRotateAnticlockwiseOutline18 className="h-3 w-3" />
          Reset
        </button>
      </div>
      {/* Connector selector — only when action uses a connector */}
      {connectorType && <ConnectorSelector connectorType={connectorType} value={testConnectorId} onChange={setTestConnectorId} label="Connector" description="Select a connector for this test" required={connectorRequired} />}
      {/* Warning when testing is disabled for this action */}
      {!isTestable && testDisabledReason && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/30">
          <div className="flex items-start gap-2">
            <IconAlertWarningOutline18 className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-sm text-amber-800 dark:text-amber-200">{testDisabledReason}</p>
          </div>
        </div>}
      {/* Run Test button */}
      <Button onClick={handleRunTest} disabled={!canRunTest} className="w-full" title={!canRunTest && connectorRequired && !testConnectorId ? "Select a connector first" : undefined}>
        {isLoading ? <div className="flex items-center gap-2">
            <PixelLoader size="sm" />
            Running Test...
          </div> : <div className="flex items-center gap-2">
            <IconPlay className="h-4 w-4" />
            Run Test
          </div>}
      </Button>
      {/* Test result display */}
      {/* Consecutive failure warning */}
      <ConfirmDialog isOpen={showFailureWarning} title="Multiple test failures" message={`${consecutiveFailuresRef.current} consecutive test failures. Consider reviewing your configuration or inputs before testing again.`} confirmText="Test Anyway" cancelText="Cancel" onConfirm={handleRunTestAnyway} onCancel={() => setShowFailureWarning(false)} variant="warning" />
      {testResult.status !== "idle" && <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="label label-xs">Result</h4>
            {testResult.duration !== undefined && <span className="label-xs flex items-center gap-1">
                <IconClockOutline18 className="h-3 w-3" />
                {formatDuration(testResult.duration)}
              </span>}
          </div>

          <Accordion type="single" collapsible defaultValue="result">
            <AccordionItem value="result" className="border-ds-border-default rounded-lg border">
              <AccordionTrigger className="px-3 py-2 hover:no-underline">
                <div className="flex items-center gap-2">
                  {testResult.status === "loading" && <PixelLoader size="sm" color="blue" />}
                  {testResult.status === "success" && <IconCheckOutline18 className="h-4 w-4 text-green-500" />}
                  {testResult.status === "error" && <IconXmarkOutline18 className="h-4 w-4 text-red-500" />}
                  <span className={`text-sm font-medium ${testResult.status === "success" ? "text-green-600 dark:text-green-400" : testResult.status === "error" ? "text-red-600 dark:text-red-400" : "text-blue-600 dark:text-blue-400"}`}>
                    {testResult.status === "loading" ? "Running..." : testResult.status === "success" ? "Success" : testResult.error ? summarizeError(testResult.error) : "Error"}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="border-ds-border-default border-t px-0">
                {testResult.status === "success" && testResult.result && <ResultJsonBlock data={testResult.result} syntaxStyle={syntaxStyle} />}
                {testResult.status === "error" && testResult.error && <div className="p-3">
                    <UnifiedErrorPanel error={testResult.error} variant="inline" isTestRun={true} showVersionCompare={false} />
                  </div>}
                {testResult.status === "error" && testResult.result && <ResultJsonBlock data={testResult.result} syntaxStyle={syntaxStyle} />}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <Button variant="white" size="sm" className="w-full" onClick={resetTest}>
            <div className="text-ds-text-subtle hover:text-ds-text-secondary dark:text-ds-text-disabled flex items-center gap-2 text-xs dark:hover:text-zinc-300">
              <IconArrowRotateAnticlockwiseOutline18 className="h-3 w-3" />
              Clear result
            </div>
          </Button>
        </div>}
    </div>;
}